import { PlusOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  _propagationTypeMapping,
  PROPAGATION_ICON_MAP,
  propagationTypeMapping,
  truncatePropagationNotes,
} from 'horizon/components/Damages/utils';
import type { AtlasGqlHorizonDamagePropagation } from 'types/atlas-graphql';

import { EmptyPropagationTag, StyledPropagationTag, TagButton } from './PropagationTag.style';

type Props = {
  propagation: Pick<AtlasGqlHorizonDamagePropagation, 'type' | 'notes'> | null | undefined;
  onEdit?: () => void;
  shouldShowNotes?: boolean;
  'data-testid'?: string;
};

export const PropagationTag = ({
  propagation,
  onEdit,
  shouldShowNotes = true,
  'data-testid': testId,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!propagation?.type) {
    // The aria-label keeps the icon's name out of the button's name.
    return onEdit ? (
      <Tooltip title={t('damage.propagation.addTooltip', 'Click to add a Propagation')}>
        <TagButton
          aria-label={t('damage.propagation.add', 'Add Propagation')}
          data-testid={testId}
          onClick={onEdit}
        >
          <EmptyPropagationTag icon={<PlusOutlined />}>
            {t('damage.propagation.add', 'Add Propagation')}
          </EmptyPropagationTag>
        </TagButton>
      </Tooltip>
    ) : (
      <EmptyPropagationTag data-testid={testId}>
        {t('damage.propagation.none', 'No Propagation')}
      </EmptyPropagationTag>
    );
  }

  const { icon: Icon, color } = PROPAGATION_ICON_MAP[propagation.type];
  const notes = shouldShowNotes && propagation.notes;
  const typeLabel =
    propagationTypeMapping[propagation.type] ?? _propagationTypeMapping[propagation.type];
  const tag = (
    <StyledPropagationTag
      data-testid={onEdit ? undefined : testId}
      icon={<Icon style={{ color: theme[color] as string }} />}
    >
      {typeLabel}
    </StyledPropagationTag>
  );

  return (
    <Tooltip
      title={
        (notes || onEdit) && (
          <>
            {notes && (
              <div>
                {t('damage.propagation.notesTooltip', 'Notes: {{notes}}', {
                  notes: truncatePropagationNotes(notes),
                })}
              </div>
            )}
            {onEdit && (
              <div>{t('damage.propagation.editTooltip', 'Click to edit Propagation')}</div>
            )}
          </>
        )
      }
    >
      {onEdit ? (
        // Names the action, which the pill's text doesn't, and keeps the icon's name out of it.
        <TagButton
          aria-label={t('damage.propagation.editLabel', 'Edit Propagation: {{type}}', {
            type: typeLabel,
          })}
          data-testid={testId}
          onClick={onEdit}
        >
          {tag}
        </TagButton>
      ) : (
        tag
      )}
    </Tooltip>
  );
};
